import { useCallback, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleShowDialogs } from 'forms/hooks';
import { compose, filter, includes, sort } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { EMPTY_VALUES } from 'constants/general';
import taskSearch from 'features/views/taskSearch';
import { prefillFormWithPreviouDataAndRefetch } from 'features/views/taskSearch/actions';
import { requiredFields } from 'features/views/taskSearch/constants';
import { FIELDS, TASK_SEARCH_FORM_NAME } from 'features/views/taskSearch/form';
import { getAllowedTaskSearchIds } from 'features/views/user/selectors';
import { getCodelistByName, sortAlphabeticallyBy } from 'utils';

const { additionalTaskStatuses } = taskSearch.constants;

export const TaskSearchForm = () => {
	const dispatch = useDispatch();
	const next = useControlNext(TASK_SEARCH_FORM_NAME);
	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const taskSearchAclIds = useSelector(getAllowedTaskSearchIds);

	const taskTypes = useSelector(getCodelistByName(codelists.taskTypes));
	const taskSubtypes = useSelector(getCodelistByName(codelists.taskSubtypes));
	const taskStatuses = useSelector(getCodelistByName(codelists.taskStatus));
	const finishTypes = useSelector(getCodelistByName(codelists.finishTypes));

	useEffect(() => {
		if (formIsInitiated) {
			// going back from detail
			dispatch(prefillFormWithPreviouDataAndRefetch());
		}
	}, [dispatch, formIsInitiated]);

	const handleTaskTypeChange = useCallback(() => {
		next(setValue({ fieldName: FIELDS.finishTypeId, value: null }));
		next(setValue({ fieldName: FIELDS.taskSubTypeId, value: null }));
	}, [next]);

	const finishTypesOptionsByTaskType = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => item.taskTypeId === Number(formData?.taskType) && item.description),
			)(finishTypes),
		[finishTypes, formData?.taskType],
	);

	const taskTypesOptionsByPermittedTypes = useMemo(() => {
		// informative = 2 > lead task
		const tasksWithoutLeads = taskTypes.filter((option) => option.informative !== 2);
		if (includes('all', taskSearchAclIds)) {
			return compose(sort(sortAlphabeticallyBy('description')))(tasksWithoutLeads);
		} else {
			return compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => includes(item.id, taskSearchAclIds)),
			)(tasksWithoutLeads);
		}
	}, [taskSearchAclIds, taskTypes]);

	const taskSubtypesOptionsBySelectedTaskType = useMemo(() => {
		const filteredSubtypes = taskSubtypes.filter(({ taskTypeIds }) => {
			const taskTypes = taskTypeIds.split(',');
			return includes(formData?.taskType, taskTypes);
		});

		return compose(sort(sortAlphabeticallyBy('description')))(filteredSubtypes);
	}, [formData?.taskType, taskSubtypes]);

	const taskStatusOptions = useMemo(() => {
		if (!isNilOrEmpty(taskStatuses)) {
			const [newToFinish, toFinish, finished] = taskStatuses;
			const addedTaskStatusOptions = [
				{ description: getLabelMessage('taskSearch:inTime'), id: additionalTaskStatuses.IN_TIME },
				{ description: getLabelMessage('taskSearch:afterDeadline'), id: additionalTaskStatuses.AFTER_DEADLINE },
			];

			const allOptions = [newToFinish, toFinish, ...addedTaskStatusOptions, finished];

			return allOptions;
		} else return EMPTY_VALUES.ARRAY;
	}, [taskStatuses]);

	const handleReset = useCallback(() => {
		dispatch(taskSearch.actions.resetCriteria());
	}, [dispatch]);

	const handleSubmit = useCallback(() => {
		dispatch(taskSearch.actions.search());
	}, [dispatch]);

	const handleShowDialogsForSearchSubmit = useHandleShowDialogs({
		page: TASK_SEARCH_FORM_NAME,
		mapper: {
			atLeastOneOfRequiredFields: requiredFields,
		},
		continueFn: handleSubmit,
	});

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('taskSearch:policyNo')}
								validationDisplay={!formData?.policyNumber ? validationDisplayTypes.HIDDEN : undefined}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskType}
							label={getLabelMessage('taskSearch:taskType')}
							options={taskTypesOptionsByPermittedTypes}
							onChange={handleTaskTypeChange}
							labelPropName="description"
							validationDisplay={!formData?.taskType ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskSubTypeId}
							label={getLabelMessage('taskSearch:taskSubtypeId')}
							options={taskSubtypesOptionsBySelectedTaskType}
							disabled={isNilOrEmptyString(formData?.taskType)}
							labelPropName="description"
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.id}
							label={getLabelMessage('taskSearch:id')}
							validationDisplay={!formData?.id ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.ID,
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.statusId}
							label={getLabelMessage('taskSearch:statusId')}
							options={taskStatusOptions}
							labelPropName="description"
							multiple
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.finishTypeId}
							label={getLabelMessage('taskSearch:finishTypeId')}
							disabled={isNilOrEmptyString(formData?.taskType)}
							options={finishTypesOptionsByTaskType}
							multiple
							labelPropName="description"
							valuePropName="finishTypeId"
						/>
					</Grid>
					<Grid size={4}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('taskSearch:surname')}
							validationDisplay={!formData?.surname ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={4}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('taskSearch:firstName')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:birthNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.personIn}
								label={getLabelMessage('taskSearch:personIn')}
								validationDisplay={!formData?.personIn ? validationDisplayTypes.HIDDEN : undefined}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.BIRTH_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={4}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('taskSearch:businessName')}
							validationDisplay={!formData?.businessName ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:taxNumber')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.businessIn}
								label={getLabelMessage('taskSearch:businessIn')}
								validationDisplay={!formData?.businessIn ? validationDisplayTypes.HIDDEN : undefined}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.TAX_NUMBER,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.municipality}
							label={getLabelMessage('taskSearch:municipality')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.MUNICIPALITY,
								},
							}}
						/>
					</Grid>
					<Tooltip title={getLabelMessage('tooltips:postalCode')}>
						<Grid size={1}>
							<TextField
								size="small"
								fieldName={FIELDS.zipCode}
								label={getLabelMessage('taskSearch:zipCode')}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.ZIP_CODE,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.login}
							label={getLabelMessage('taskSearch:login')}
							validationDisplay={!formData?.login ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.LOGIN,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.queueGroupCd}
							label={getLabelMessage('taskSearch:queueGroupCd')}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.QUEUE_GROUP_CD,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.createdFrom}
							label={getLabelMessage('taskSearch:createdFrom')}
							maxDate={new Date()}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.createdTo}
							label={getLabelMessage('taskSearch:createdTo')}
							maxDate={new Date()}
						/>
					</Grid>
					<Grid size={12}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={2}>
								<Button variant="outlined" onClick={handleReset} fullWidth>
									{getLabelMessage('common:resetButton')}
								</Button>
							</Grid>
							<Grid size={2}>
								<SubmitButton
									variant="contained"
									onClick={handleSubmit}
									onClickError={handleShowDialogsForSearchSubmit}
									fullWidth
									text={getLabelMessage('common:searchButton')}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default TaskSearchForm;
